import {
  generateRowsForTableSkeleton,
  Skeleton,
  TableCellSkeleton,
  TableComponent,
  useColumnsCustomRenderConfig,
  type Column,
} from '@innovamat/glimmer-components';
import { useDevice } from '@innovamat/hooks';
import { useTranslation } from 'react-i18next';

import { TEST_STATUS } from '../../../../../../modules/test/test';
import {
  ContentContainer,
  Description,
  FlexContainer,
  InfoContainer,
  InfoTagsContainer,
} from '../../mock-exams-drawer';

type SkeletonRow = {
  id: string;
  firstName: string;
  lastName: string;
  status: typeof TEST_STATUS.NOT_STARTED;
  rowNumber?: number;
};

function TableSkeleton(): JSX.Element {
  const { t } = useTranslation();
  const { isMobile } = useDevice();

  const mockObject: SkeletonRow = {
    id: '',
    firstName: '',
    lastName: '',
    status: TEST_STATUS.NOT_STARTED,
  };

  const rowsNumber = 6;

  const columns: Column<SkeletonRow>[] = [
    {
      id: 'firstName',
      label: t('mock-exams-drawer.table.first-name.label'),
      sortable: true,
      sortIgnoreCase: true,
      width: 112,
      minWidth: 112,
      sticky: !isMobile,
    },
    {
      id: 'lastName',
      label: t('mock-exams-drawer.table.last-name.label'),
      sortable: true,
      sortIgnoreCase: true,
      width: 112,
      minWidth: 112,
      sticky: !isMobile,
    },
    {
      id: 'status',
      label: t('common.status'),
      width: 145,
      minWidth: 145,
      sticky: !isMobile,
    },
  ];

  const customRenderConfig = {
    firstName: (_: string, row: SkeletonRow) => (
      <TableCellSkeleton isLastRow={row?.rowNumber === rowsNumber} />
    ),
    lastName: (_: string, row: SkeletonRow) => (
      <TableCellSkeleton isLastRow={row?.rowNumber === rowsNumber} />
    ),
    status: (_: string, row: SkeletonRow) => (
      <TableCellSkeleton isLastRow={row?.rowNumber === rowsNumber} />
    ),
  };

  const tableColumns = useColumnsCustomRenderConfig(
    customRenderConfig,
    columns
  );

  const rows = generateRowsForTableSkeleton(rowsNumber, mockObject);

  return (
    <TableComponent
      columns={tableColumns}
      id="tests-and-results-table-skeleton"
      key="tests-and-results-table-skeleton"
      rows={rows}
      style={{ overflow: 'hidden' }}
    />
  );
}

function TestDrawerSkeleton(): JSX.Element {
  return (
    <ContentContainer>
      <FlexContainer>
        <InfoContainer>
          <Description>
            <Skeleton width="100%" height="24" radius="8" />
            <Skeleton width="100%" height="24" radius="8" />
          </Description>
          <InfoTagsContainer>
            <Skeleton width="180px" height="24" radius="8" />
            <Skeleton width="180px" height="24" radius="8" />
            <Skeleton width="180px" height="24" radius="8" />
          </InfoTagsContainer>
        </InfoContainer>
        <Skeleton width="180px" height="24" radius="8" />
      </FlexContainer>
      <TableSkeleton />
    </ContentContainer>
  );
}

export { TestDrawerSkeleton };
